import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

import { IGNORED_NOTE_IDENTIFIERS_FOR_QUIZ, NoteFieldIdentifiers } from "../constants";
import { type AnalyticsMetadata, type FacilityNote } from "../types";
import { quizGenerationResponseLogSchema } from "./usePollQuizGenerationStatus";

interface UpdateFacilityNotesParams {
  oldFacilityNotes: FacilityNote[];
  newFacilityNotes: FacilityNote[];
  analyticsMetadata: AnalyticsMetadata;
}

const updateFacilityNotesRequestSchema = z.object({
  "atomic:operations": z.array(
    z.union([
      z.object({
        op: z.literal("add"),
        data: z.object({
          type: z.literal("FacilityNote"),
          attributes: z.object({
            categoryId: z.string(),
            note: z.string(),
            workplaceId: z.string(),
          }),
        }),
      }),
      z.object({
        op: z.literal("update"),
        data: z.object({
          id: z.string(),
          type: z.literal("FacilityNote"),
          attributes: z.object({
            categoryId: z.string(),
            note: z.string(),
            workplaceId: z.string(),
          }),
        }),
      }),
      z.object({
        op: z.literal("remove"),
        data: z.object({
          id: z.string(),
          type: z.literal("FacilityNote"),
          attributes: z.object({
            workplaceId: z.string(),
          }),
        }),
      }),
      z.object({
        op: z.literal("add"),
        data: z.object({
          type: z.literal("workplace-quiz-generation-request"), // We want to generate quiz whenever we add quiz
          attributes: z.object({
            workplaceId: z.string(),
          }),
        }),
      }),
    ])
  ),
});

type UpdateFacilityNotesRequest = z.infer<typeof updateFacilityNotesRequestSchema>;

const updateFacilityNotesResponseSchema = z.object({
  "atomic:results": z.array(
    z.object({
      data: z.union([
        z.object({
          type: z.literal("FacilityNote"),
          id: z.string(),
        }),
        quizGenerationResponseLogSchema,
      ]),
    })
  ),
});

type UpdateFacilityNotesResponse = z.infer<typeof updateFacilityNotesResponseSchema>;

function getOperationsFromFacilityNotes(params: {
  facilityId: string;
  oldFacilityNotes: FacilityNote[];
  newFacilityNotes: FacilityNote[];
}): UpdateFacilityNotesRequest {
  const { facilityId, oldFacilityNotes, newFacilityNotes } = params;

  const operations: UpdateFacilityNotesRequest["atomic:operations"] = [];
  const changedNoteIdentifiers: string[] = [];

  for (const identifier of Object.values(NoteFieldIdentifiers)) {
    const oldNote = oldFacilityNotes.find((oldNote) => oldNote.identifier === identifier);
    const newNote = newFacilityNotes.find((newNote) => newNote.identifier === identifier);

    if (!isDefined(newNote) || !isDefined(oldNote)) {
      // We cannot do anything in this scenario, so just ignore. We cannot even create
      // a new note since we don't have a valid `categoryId`.
      continue;
    }

    if (!isDefined(oldNote.noteId)) {
      if (isDefined(newNote.note) && newNote.note !== "") {
        operations.push({
          op: "add",
          data: {
            type: "FacilityNote",
            attributes: {
              categoryId: newNote.categoryId,
              note: newNote.note,
              workplaceId: facilityId,
            },
          },
        });
        changedNoteIdentifiers.push(newNote.identifier);
      }
    } else if (isDefined(oldNote.note) && (!isDefined(newNote.note) || newNote.note === "")) {
      operations.push({
        op: "remove",
        data: {
          id: oldNote.noteId,
          type: "FacilityNote",
          attributes: {
            workplaceId: facilityId,
          },
        },
      });
      changedNoteIdentifiers.push(oldNote.identifier);
    } else if (oldNote.note !== newNote.note && isDefined(newNote.note)) {
      operations.push({
        op: "update",
        data: {
          id: oldNote.noteId,
          type: "FacilityNote",
          attributes: {
            categoryId: oldNote.categoryId,
            note: newNote.note,
            workplaceId: facilityId,
          },
        },
      });
      changedNoteIdentifiers.push(oldNote.identifier);
    }
  }

  // We don't want to generate quiz if every changed notes are to be ignored for quiz
  const isEveryNoteIsIgnoredForQuiz = changedNoteIdentifiers.every((changedIdentifier) =>
    // eslint-disable-next-line unicorn/prefer-includes
    IGNORED_NOTE_IDENTIFIERS_FOR_QUIZ.some(
      (ignoredNoteIdentifier) => ignoredNoteIdentifier === changedIdentifier
    )
  );
  if (changedNoteIdentifiers.length > 0 && !isEveryNoteIsIgnoredForQuiz) {
    operations.push({
      op: "add",
      data: {
        type: "workplace-quiz-generation-request",
        attributes: {
          workplaceId: facilityId,
        },
      },
    });
  }

  return {
    "atomic:operations": operations,
  };
}

const UPDATE_FACILITY_NOTES_URL = `${environmentConfig.REACT_APP_API_MAIN_URL}/api/operations`;

export function useUpdateFacilityNotes(
  facilityId: string,
  options: UseMutationOptions<UpdateFacilityNotesResponse, string, UpdateFacilityNotesParams> = {}
) {
  const { showErrorToast } = useToast();

  return useMutation({
    mutationFn: async (params) => {
      const { oldFacilityNotes, newFacilityNotes, analyticsMetadata } = params;
      const data = getOperationsFromFacilityNotes({
        oldFacilityNotes,
        newFacilityNotes,
        facilityId,
      });

      if (data["atomic:operations"].length === 0) {
        return {
          "atomic:results": [],
        }; // the API doesn't accept an empty array
      }

      const result = await post({
        url: UPDATE_FACILITY_NOTES_URL,
        data,
        requestSchema: updateFacilityNotesRequestSchema,
        responseSchema: updateFacilityNotesResponseSchema,
      });

      logEvent(APP_V2_USER_EVENTS.FACILITY_RULES_AI_NOTES_UPDATED, {
        ...analyticsMetadata,
        numberOfSectionsUpdated: data["atomic:operations"].length,
        oldFacilityNotes: JSON.stringify(oldFacilityNotes),
        newFacilityNotes: JSON.stringify(newFacilityNotes),
        operations: JSON.stringify(
          data["atomic:operations"].filter((operation) => operation.data.type === "FacilityNote")
        ),
      });
      return result.data;
    },
    onError: () => {
      showErrorToast("Something went wrong. Try saving again or contact support.");
    },
    ...options,
  });
}
